import * as React from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';

const SIDEBAR_INSET_SELECTOR = '[data-slot="sidebar-inset"]';
const FALLBACK_CONTAINER_SELECTOR = 'body';

type DockEntry = {
  dock: HTMLDivElement;
  inset: HTMLElement;
  refCount: number;
  previousPaddingBottom: string;
  dockResizeObserver: ResizeObserver;
  insetResizeObserver: ResizeObserver;
  onWindowResize: () => void;
};

/**
 * One shared dock per inset. Every BottomBar portals its content into the same
 * dock so multiple bars stack on top of each other instead of overlapping and
 * fighting over the inset padding.
 */
const dockRegistry = new Map<HTMLElement, DockEntry>();

function resolveInset(): HTMLElement | null {
  const inset = document.querySelector<HTMLElement>(SIDEBAR_INSET_SELECTOR);
  if (inset) {
    return inset;
  }
  console.warn(
    '[BottomBar.tsx] No sidebar inset found, using body as fallback.',
  );
  return document.querySelector<HTMLElement>(FALLBACK_CONTAINER_SELECTOR);
}

function acquireDock(inset: HTMLElement): HTMLDivElement {
  const existing = dockRegistry.get(inset);
  if (existing) {
    existing.refCount += 1;
    return existing.dock;
  }

  const dock = document.createElement('div');
  dock.dataset.slot = 'bottom-bar-dock';
  dock.style.position = 'fixed';
  dock.style.bottom = '0';
  dock.style.zIndex = '20';
  dock.style.display = 'flex';
  dock.style.flexDirection = 'column';
  inset.appendChild(dock);

  const updateInsetPadding = () => {
    inset.style.paddingBottom = `${dock.offsetHeight}px`;
  };

  const updateDockOffsets = () => {
    const rect = inset.getBoundingClientRect();
    dock.style.left = `${rect.left}px`;
    dock.style.right = `${Math.max(0, window.innerWidth - rect.right)}px`;
  };

  const dockResizeObserver = new ResizeObserver(updateInsetPadding);
  dockResizeObserver.observe(dock);
  const insetResizeObserver = new ResizeObserver(updateDockOffsets);
  insetResizeObserver.observe(inset);
  const onWindowResize = () => updateDockOffsets();
  window.addEventListener('resize', onWindowResize);

  updateInsetPadding();
  updateDockOffsets();

  dockRegistry.set(inset, {
    dock,
    inset,
    refCount: 1,
    previousPaddingBottom: inset.style.paddingBottom,
    dockResizeObserver,
    insetResizeObserver,
    onWindowResize,
  });

  return dock;
}

function releaseDock(inset: HTMLElement): void {
  const entry = dockRegistry.get(inset);
  if (!entry) {
    return;
  }

  entry.refCount -= 1;
  if (entry.refCount > 0) {
    return;
  }

  entry.dockResizeObserver.disconnect();
  entry.insetResizeObserver.disconnect();
  window.removeEventListener('resize', entry.onWindowResize);
  entry.dock.remove();
  inset.style.paddingBottom = entry.previousPaddingBottom;
  dockRegistry.delete(inset);
}

type BottomBarProps = React.PropsWithChildren<{
  className?: string;
}>;

/**
 * Renders the children in a fixed position at the bottom of the right panel.
 * Will search for the sidebar inset or the body as a fallback. Multiple
 * BottomBars share a single dock and stack vertically within it.
 */
export function BottomBar({ children, className }: BottomBarProps) {
  const [dock, setDock] = React.useState<HTMLDivElement | null>(null);

  React.useEffect(() => {
    const inset = resolveInset();
    if (!inset) {
      return;
    }
    const acquired = acquireDock(inset);
    setDock(acquired);
    return () => {
      setDock(null);
      releaseDock(inset);
    };
  }, []);

  if (!dock) {
    return null;
  }

  return createPortal(
    <div
      data-slot="bottom-bar"
      className={cn('tw:animate-fade-in tw:animation-duration-200', className)}
    >
      {children}
    </div>,
    dock,
  );
}
